import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const [settings, setSettings] = useState({
    footer_phone: '91 125 63 52 / 678 744 482',
    footer_email: 'reservas@viajesensueno.com',
    footer_address: 'C./ Cosme Damian Churruca 6, 28660 Boadilla del Monte - Madrid'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    // Check for subject in URL state or search params
    const params = new URLSearchParams(location.search);
    const subjectParam = params.get('subject');
    const stateSubject = location.state?.subject;
    
    if (subjectParam || stateSubject) {
      setFormData(prev => ({
        ...prev,
        subject: subjectParam || stateSubject
      }));
    }
  }, [location]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (res.data) {
          setSettings(prev => ({
            ...prev,
            footer_phone: res.data.footer_phone || prev.footer_phone,
            footer_email: res.data.footer_email || prev.footer_email,
            footer_address: res.data.footer_address || prev.footer_address
          }));
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error('Error al enviar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img
          src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?auto=format&fit=crop&q=80&w=2000"
          alt="Contacto"
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 className="text-4xl lg:text-6xl font-serif text-white mb-4" data-testid="contact-title">
              Contacto
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              Estamos aquí para ayudarte a planificar tu viaje perfecto
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
                  Información de contacto
                </h2>
                <p className="text-muted-foreground mb-8">
                  ¿Tienes alguna pregunta? No dudes en contactarnos. Estaremos encantados de ayudarte.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Dirección</h3>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {settings.footer_address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Teléfono</h3>
                    <a 
                      href={`tel:${settings.footer_phone.split('/')[0].trim()}`} 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {settings.footer_phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Email</h3>
                    <a 
                      href={`mailto:${settings.footer_email}`} 
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {settings.footer_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">Horario</h3>
                    <p className="text-muted-foreground">
                      Lunes - Viernes: 9:00 - 20:00<br />
                      Sábados: 10:00 - 14:00<br />
                      Servicio de Asistencia al viajero 24x7
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="contact-form">
                <h2 className="text-2xl font-serif font-semibold text-primary mb-6">
                  Envíanos un mensaje
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Nombre completo</label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Tu nombre"
                        data-testid="contact-name"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="tu@email.com"
                        data-testid="contact-email"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Teléfono (opcional)</label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+34 612 345 678"
                        data-testid="contact-phone"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Asunto</label>
                      <Input
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="¿En qué podemos ayudarte?"
                        data-testid="contact-subject"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mensaje</label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Cuéntanos más sobre tu consulta..."
                      data-testid="contact-message"
                      className="form-textarea"
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="btn-accent py-6 px-10"
                  >
                    {submitting ? 'Enviando...' : (
                      <>
                        Enviar mensaje
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
